<template>
    <div>
        <div v-if="visualizationError">
            <div class="alert alert-danger mb-3" role="alert">
                <i class="fa fa-exclamation-triangle me-1"></i>
                {{ visualizationError }}
            </div>
        </div>
        <div v-if="outputData">
            <table class="table table-bordered visualization-table mb-0">
                <thead>
                    <tr>
                        <th v-for="(column, index) in outputColumns" :key="index">{{ column.title }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, rowIndex) in outputData.slice(0, previewRows)" :key="rowIndex">
                        <td v-for="(column, columnIndex) in outputColumns"
                            :class="$store.state.darkMode ? '' : 'bg-white'" :key="`${rowIndex},${columnIndex}`">
                            {{ row[column.data] }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <small v-if="outputData.length > previewRows"
                class="text-muted d-flex align-items-center justify-content-center mt-1">
                <i class="fa fa-circle-info me-2"></i>
                Showing {{ previewRows }} of {{ outputData.length }} rows. Export to view full results.
            </small>
        </div>
        <div v-else>
            <div v-if="queryRunning" class="text-center py-3 rounded border border-dashed"
                :class="$store.state.darkMode ? 'dark-mode-bg' : 'bg-white'">
                <span>
                    <div class="spinner-border spinner-border-sm me-2" role="status" style="width: 1em; height: 1em;">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading visualization...</span>
                </span>
            </div>
            <div v-else>
                <div class="text-center py-3 rounded border border-dashed"
                    :class="$store.state.darkMode ? 'dark-mode-bg' : 'bg-white'">
                    <span class="text-muted">
                        <i class="fa fa-info-circle"></i>
                        No data found!
                    </span>
                </div>
            </div>
        </div>
    </div>
    <div class="d-flex align-items-center justify-content-end gap-2 pb-3 mt-2">
        <button v-if="outputData" class="btn btn-secondary" @click="downloadResults" :disabled="exporting">
            <span v-if="exporting">
                <div class="spinner-border spinner-border-sm me-2" role="status" style="width: 1em; height: 1em;">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </span>
            Export
        </button>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import csv from '../../utils/tools/csv';
export default {
    name: 'Job Stack Deployment Visualization',
    props: ["loading", "deployment", "jobs", "fetchVisualizationTrigger", "queryRunning", "queryId", "outputData", "outputColumns", "isPartialResult", "visualizationError"],
    data() {
        return {
            exporting: false,
            previewRows: 50,
        };
    },
    methods: {
        ...mapActions({
            fetchFullQueryFile: "amdp/fetchFullQueryFile",
        }),
        async downloadResults() {
            this.exporting = true;
            try {
                // if partial result, get all results and download
                if (this.isPartialResult) {
                    const response = await this.fetchFullQueryFile(this.queryId);
                    if (response.https) {
                        const url = response.https;
                        const a = document.createElement("a");
                        a.href = url;
                        a.click();
                        window.URL.revokeObjectURL(url); // clean up the URL
                        a.remove(); // remove the element from the DOM
                    } else {
                        console.error("Failed to download results, Try again!");
                    }
                }
                // else download results immediately
                else {
                    let header = []
                    if (this.outputData.length > 0) {
                        Object.keys(this.outputData[0]).map((value) => header[value] = value)
                        csv.exportCSV(`${this.$store.state.currentClient.cid}_${this.pipelineJobConfig.name}_query`, [...header, ...this.outputData])
                    } else {
                        this.$store.dispatch("addToast", {
                            message: "No data to export",
                            type: "info",
                        });
                    }
                }
            } catch (error) {
                console.error("Error exporting results: ", error);
                this.$store.dispatch("addToast", {
                    message: "Failed to download results. Please retry or contact support.",
                    type: "error",
                });
            } finally {
                this.exporting = false;
            }
        },
    },
    computed: {
        pipelineJobConfig() {
            return Object.values(this.jobs).find(job => job.kind?.toLowerCase() === 'pipeline');
        },
    },
    watch: {
        fetchVisualizationTrigger: {
            handler(newVal) {
                if (newVal) {
                    this.$emit('visualizeTrigger');
                }
            },
            immediate: true,
        },
    },
    beforeMount() {
        this.$emit('setVisualizationButton', true);
        if (this.fetchVisualizationTrigger) {
            this.$emit('visualizeTrigger');
        }
    },
    beforeUnmount() {
        this.$emit('setVisualizationButton', false);
    },
    emits: ['setVisualizationButton', 'visualizeTrigger'],
}
</script>

<style scoped>
.visualization-table> :not(caption)>*>*:not(th) {
    padding: 0.5rem 0.5rem;
    font-size: 0.8rem;
}

.visualization-table>*>th {
    font-size: 0.9rem;
    font-weight: bold;
}

.dark-mode-bg {
    background-color: #212529;
}
</style>